import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@/components/Elements';
import { REQUEST_FAILED, REQUEST_SUCCESS } from '@/config/appConstants';
import { renderField } from '@/utils/forms';

const CouponErrorMessage = styled.div`
  .coupon--error__message {
    color: var(--md-red-color);
  }
`;

const CouponSuccessMessage = styled.div`
  .coupon--success__message {
    color: var(--md-linksAlt-color);
    position: relative;
  }
`;

export type CouponStatusProps = typeof REQUEST_SUCCESS | typeof REQUEST_FAILED;

export type CouponFormProps = {
  className?: string;
  onSubmit: (values: any) => void;
  couponStatus?: CouponStatusProps;
  lastValidCoupon?: string;
  currentCoupon?: string;
  isLoading?: boolean;
};

export const CouponForm = ({
  className = '',
  onSubmit,
  couponStatus,
  lastValidCoupon,
  currentCoupon,
  isLoading,
}: CouponFormProps) => {
  const { t } = useTranslation();
  const MainForm = () => {
    const onFormSubmit = (values: any) => {
      onSubmit && onSubmit(values);
    };

    const formikData = {
      initialValues: {
        customCoupon: currentCoupon || '',
      },
      onSubmit: onFormSubmit,
    };

    const getCouponErrors = () => {
      if (currentCoupon && couponStatus === REQUEST_FAILED) {
        return (
          <CouponErrorMessage>
            <div className="coupon--error__message">
              <p className="text-xs">{t('Forms.Coupon.couponError')}</p>
            </div>
          </CouponErrorMessage>
        );
      }
    };

    const getCouponSuccess = () => {
      if (currentCoupon && couponStatus === REQUEST_SUCCESS) {
        return (
          <CouponSuccessMessage>
            <div className="coupon--success__message">
              <p className="text-xs">{t('Forms.Coupon.couponApplied')}</p>
            </div>
          </CouponSuccessMessage>
        );
      }
    };

    const formik: any = useFormik(formikData);

    const isClearCouponButtonVisible = () => {
      if ((currentCoupon && couponStatus === REQUEST_SUCCESS) || lastValidCoupon) {
        return true;
      }

      return false;
    };

    const getClearCouponButton = () => {
      if (isClearCouponButtonVisible()) {
        return (
          <div className="clear__coupon__message">
            <a
              tabIndex={0}
              role="button"
              className="text-sm"
              onClick={() => {
                formik.setFieldValue('customCoupon', '');
                formik.submitForm();
              }}
              onKeyDown={() => {
                formik.setFieldValue('customCoupon', '');
                formik.submitForm();
              }}
            >
              <i className="ri-close-line" />
            </a>
          </div>
        );
      }
    };

    return (
      <form onSubmit={formik.handleSubmit} className="">
        {isLoading ? '' : getClearCouponButton()}

        <div className="md__payments__modal__coupon--row">
          {renderField(formik, 'customCoupon', {
            showLabel: false,
            customClasses: 'md__payments__modal__coupon--field',
          })}

          <Button
            variant="secondary"
            type="submit"
            size="free"
            className=""
            loadingText={t('Forms.Coupon.validating')}
            isLoading={isLoading}
          >
            {t('Forms.Coupon.applyCoupon')}
          </Button>
        </div>
        {!isLoading && getCouponErrors()}
        {!isLoading && getCouponSuccess()}
      </form>
    );
  };

  return (
    <div className={className}>
      <MainForm />
    </div>
  );
};
